const ACRONYM_MAP = {
  agb: "AGB",
  b2b: "B2B",
  b2c: "B2C",
  ceo: "CEO",
  faq: "FAQ",
  hr: "HR",
  it: "IT",
};

const KNOWN_DOMAIN_TITLES = [
  {
    pattern: /(^|\.)2pi-labs\.com$/i,
    defaultTitle: "2pi-Labs",
  },
  {
    pattern: /(^|\.)citex-group\.com$/i,
    defaultTitle: "CiTEX Gruppe",
    footerTitle: "Ein Unternehmen der CiTEX Gruppe",
  },
  {
    pattern: /(^|\.)idoo\.global$/i,
    defaultTitle: "iDOO",
  },
  {
    pattern: /(^|\.)inoex\.de$/i,
    defaultTitle: "iNOEX",
  },
  {
    pattern: /(^|\.)pixargus\.de$/i,
    defaultTitle: "PIXARGUS",
  },
  {
    pattern: /(^|\.)extrusion360\.group$/i,
    defaultTitle: "360° Extrusion",
  },
];

const DEFAULT_FALLBACK_TITLE = "Link";
const URL_BASE = "https://local.invalid";

const safeDecode = (value = "") => {
  try {
    return decodeURIComponent(value);
  } catch (error) {
    return value;
  }
};

const formatToken = (word) => {
  if (!word) {
    return "";
  }

  return ACRONYM_MAP[word.toLowerCase()] || `${word.charAt(0).toUpperCase()}${word.slice(1)}`;
};

const formatTitle = (value = "") => {
  const normalized = safeDecode(value).replace(/[-_]+/g, " ").replace(/\s+/g, " ").trim();

  if (!normalized) {
    return "";
  }

  const directMatch = ACRONYM_MAP[normalized.toLowerCase()];

  if (directMatch) {
    return directMatch;
  }

  return normalized.split(" ").map(formatToken).join(" ");
};

const getKnownDomainTitle = (hostname = "", { isFooterLink = false } = {}) => {
  const matchedDomain = KNOWN_DOMAIN_TITLES.find(({ pattern }) => pattern.test(hostname));

  if (!matchedDomain) {
    return "";
  }

  if (isFooterLink && matchedDomain.footerTitle) {
    return matchedDomain.footerTitle;
  }

  return matchedDomain.defaultTitle;
};

const getPathSlugTitle = (pathname = "") => {
  const slug = pathname.split("/").filter(Boolean).pop();

  if (!slug) {
    return "";
  }

  return formatTitle(slug);
};

// Keep named export for existing imports across multiple components.
export const getLinkTitleFromUrl = (url = "", options = {}) => {
  const { isFooterLink = false } = options;
  const input = `${url}`.trim();

  if (!input) {
    return DEFAULT_FALLBACK_TITLE;
  }

  try {
    const parsedUrl = new URL(input, URL_BASE);
    const isAbsoluteUrl = /^https?:\/\//i.test(input);
    const hostname = isAbsoluteUrl ? parsedUrl.hostname.replace(/^www\./i, "") : "";
    const pathname = parsedUrl.pathname || "";

    const knownDomainTitle = getKnownDomainTitle(hostname, { isFooterLink });

    if (knownDomainTitle) {
      return knownDomainTitle;
    }

    const slugTitle = getPathSlugTitle(pathname);

    if (slugTitle) {
      return slugTitle;
    }

    if (hostname) {
      const domainRoot = hostname.split(".")[0] || "";
      const domainTitle = formatTitle(domainRoot);

      if (domainTitle) {
        return domainTitle;
      }
    }
  } catch (error) {
    const rawFallback = input.split(/[?#]/)[0];
    const fallbackTitle = formatTitle(rawFallback.split("/").filter(Boolean).pop() || "");

    if (fallbackTitle) {
      return fallbackTitle;
    }
  }

  return DEFAULT_FALLBACK_TITLE;
};

// Normalize a footer/block link item into `{ url, title }`.
// Accepts either a raw URL string, a `{ link, header }` item from the API
// (where `link` may itself be a string or an object), or a `{ url, title }` /
// `{ publicUrl }` link object. The API-provided `header` always wins; we only
// fall back to deriving a title from the URL when no header/title is given.
export const normalizeLink = (rawLink, options = {}) => {
  if (!rawLink) {
    return { url: "", title: "" };
  }

  if (typeof rawLink === "string") {
    return {
      url: rawLink,
      title: getLinkTitleFromUrl(rawLink, options),
    };
  }

  const nested = rawLink.link && typeof rawLink.link === "object" ? rawLink.link : null;

  const url =
    (typeof rawLink.link === "string" && rawLink.link) ||
    nested?.url ||
    nested?.publicUrl ||
    rawLink.url ||
    rawLink.publicUrl ||
    "";

  const explicitTitle = rawLink.header || rawLink.title || nested?.title || "";

  return {
    url,
    title: explicitTitle || (url ? getLinkTitleFromUrl(url, options) : ""),
  };
};

export default getLinkTitleFromUrl;
